/**
 * Découpage du temps pour le tableau de bord.
 *
 * Les dates saisies dans l'application sont écrites à la suisse —
 * `31.12.2026` — et non au format ISO. Les comparer suppose donc de les
 * relire, ce que `new Date("31.12.2026")` ne fait pas : le constructeur
 * attend un mois en deuxième position et rend une date invalide. Tout passe
 * ici, pour que la règle soit écrite une fois.
 */

/** Périodes proposées, dans l'ordre où elles s'affichent. */
export const PERIODES = [
  ['mois', 'Mois'],
  ['trimestre', 'Trim.'],
  ['annee', 'Année'],
  ['tout', 'Tout'],
];

/**
 * Relit une date écrite à la suisse.
 *
 * L'année sur deux chiffres est ramenée au XXIᵉ siècle, et l'année absente à
 * l'année courante : une facture datée `03.11` vient d'être établie, elle n'a
 * pas deux mille ans.
 *
 * @param {string} d date écrite `jj.mm.aaaa`, `jj.mm.aa` ou `jj.mm`
 * @param {Date} [maintenant] date de référence, pour les tests
 * @returns {Date|null} la date relue, ou `null` si elle ne se lit pas
 */
export function litDate(d, maintenant = new Date()) {
  if (!d) {
    return null;
  }
  const p = String(d).split('.');
  if (p.length < 2) {
    return null;
  }
  const jour = Number(p[0]);
  const mois = Number(p[1]);
  if (!Number.isFinite(jour) || !Number.isFinite(mois)) {
    return null;
  }
  let annee = maintenant.getFullYear();
  if (p[2]) {
    const a = Number(p[2]);
    if (!Number.isFinite(a)) {
      return null;
    }
    annee = a < 100 ? 2000 + a : a;
  }
  const dt = new Date(annee, mois - 1, jour);
  // Le constructeur reporte sans se plaindre : le 32 janvier devient le
  // 1er février. Un tel report signale une saisie fautive, pas une date.
  if (dt.getMonth() !== mois - 1 || dt.getDate() !== jour) {
    return null;
  }
  return dt;
}

/**
 * Bornes d'une période, éventuellement décalée vers le passé.
 *
 * Le décalage sert à comparer : la même période un cran plus tôt donne le
 * point de référence d'une tendance. « Tout » n'a pas de borne — comparer
 * l'intégralité de l'historique à lui-même n'aurait pas de sens, et la
 * tendance est tue dans ce cas.
 *
 * @param {string} periode `mois`, `trimestre`, `annee` ou `tout`
 * @param {number} [decalage] nombre de périodes en arrière
 * @param {Date} [maintenant] date de référence, pour les tests
 * @returns {[Date, Date]} début et fin, bornes comprises
 */
export function bornes(periode, decalage = 0, maintenant = new Date()) {
  const an = maintenant.getFullYear();
  const mois = maintenant.getMonth();
  const finDuJour = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate(), 23, 59, 59, 999);
  if (periode === 'mois') {
    const debut = new Date(an, mois - decalage, 1);
    return [debut, finDuJour(new Date(an, mois - decalage + 1, 0))];
  }
  if (periode === 'trimestre') {
    const premierMoisDuTrimestre = Math.floor(mois / 3) * 3 - decalage * 3;
    const debut = new Date(an, premierMoisDuTrimestre, 1);
    return [debut, finDuJour(new Date(an, premierMoisDuTrimestre + 3, 0))];
  }
  if (periode === 'annee') {
    return [new Date(an - decalage, 0, 1), finDuJour(new Date(an - decalage, 11, 31))];
  }
  // « Tout » : des bornes assez larges pour ne rien exclure.
  return [new Date(1970, 0, 1), finDuJour(new Date(an + 100, 11, 31))];
}

/**
 * Dit si une date écrite tombe dans un intervalle.
 *
 * @param {string} d date écrite à la suisse
 * @param {Date} debut borne comprise
 * @param {Date} fin borne comprise
 * @param {Date} [maintenant] date de référence, pour les tests
 * @returns {boolean}
 */
export function dansPeriode(d, debut, fin, maintenant = new Date()) {
  const x = litDate(d, maintenant);
  return !!(x && x >= debut && x <= fin);
}

/**
 * Évolution d'un montant d'une période à la suivante, en pourcentage.
 *
 * Partir de zéro n'est pas une progression mesurable : le passage de rien à
 * quelque chose est rendu comme cent pour cent plutôt que comme une division
 * par zéro. La période « tout » n'a pas de précédent et ne rend rien.
 *
 * @param {string} periode période comparée
 * @param {number} courant montant de la période
 * @param {number} precedent montant de la période d'avant
 * @returns {number|null} l'évolution, ou `null` si elle n'a pas de sens
 */
export function tendance(periode, courant, precedent) {
  if (periode === 'tout') {
    return null;
  }
  if (precedent > 0) {
    return Math.round(((courant - precedent) / precedent) * 100);
  }
  // Sans periode precedente, il n'y a rien a comparer : « +100 % » sur tous
  // les indicateurs du premier mois ne disait rien.
  if (courant > 0) {
    return null;
  }
  return courant < 0 ? -100 : 0;
}

/** Noms des mois, pour intituler les lignes du détail. */
const MOIS_DETAIL = [
  'Janvier',
  'Février',
  'Mars',
  'Avril',
  'Mai',
  'Juin',
  'Juillet',
  'Août',
  'Septembre',
  'Octobre',
  'Novembre',
  'Décembre',
];

/**
 * Détail du chiffre d'affaires et du bénéfice, période par période.
 *
 * <p>Les tuiles du tableau de bord donnent un chiffre pour la période choisie :
 * elles disent où on en est, non comment on y est arrivé. Un patron qui voit
 * un mauvais trimestre veut savoir quel mois l'a fait, et si la dépense a suivi
 * la recette ou l'a devancée.
 *
 * <p>Le bénéfice est la recette moins la dépense de la même période : c'est un
 * résultat de trésorerie, non un résultat comptable — les salaires et les
 * amortissements vivent ailleurs. La comptabilité reste la source pour le
 * compte de résultat.
 *
 * @param {object} options données du détail
 * @param {string} options.periode `mois`, `trimestre` ou `annee`
 * @param {Array} options.factures factures à compter
 * @param {Array} [options.frais] dépenses à déduire
 * @param {(f: object) => number} options.ca montant retenu d'une facture
 * @param {(f: object) => number} [options.ttc] le même montant, TVA comprise
 * @param {Date} [options.maintenant] date de référence
 * @returns {{titre: string, lignes: Array<{libelle: string, ca: number, ttc: number,
 *           benefice: number, annee?: number}>, totalCa: number, totalTtc: number,
 *           totalBenefice: number}|null}
 *          le détail, ou null si la période n'en admet pas
 */
export function detailParPeriode({ periode, factures, frais, ca, ttc, maintenant = new Date() }) {
  const annee = maintenant.getFullYear();
  // Un avoir n'est pas une recette : le compter gonflerait le chiffre
  // d'affaires du mois où il a été établi.
  const pieces = (factures || []).filter((f) => f.type !== 'avoir');
  const depenses = frais || [];

  const dans = (valeur, test) => {
    const d = litDate(valeur, maintenant);
    return d ? test(d.getFullYear(), d.getMonth()) : false;
  };
  const ligne = (libelle, test) => {
    const recette = pieces.reduce((s, f) => s + (dans(f.date, test) ? ca(f) : 0), 0);
    const recetteTtc = ttc
      ? pieces.reduce((s, f) => s + (dans(f.date, test) ? ttc(f) : 0), 0)
      : recette;
    const depense = depenses.reduce(
      (s, f) => s + (dans(f.date, test) ? Number(f.montant) || 0 : 0),
      0,
    );
    return { libelle, ca: recette, ttc: recetteTtc, benefice: recette - depense };
  };

  let titre = '';
  let lignes = null;
  if (periode === 'mois') {
    titre = 'Détail mois par mois — ' + annee;
    lignes = MOIS_DETAIL.map((nom, i) => ligne(nom, (y, m) => y === annee && m === i));
  } else if (periode === 'trimestre') {
    titre = 'Détail par trimestre — ' + annee;
    lignes = [0, 1, 2, 3].map((t) =>
      ligne('T' + (t + 1), (y, m) => y === annee && Math.floor(m / 3) === t),
    );
  } else if (periode === 'annee') {
    titre = 'Détail par année';
    lignes = [annee - 2, annee - 1, annee].map((y) => ({
      ...ligne(String(y), (yy) => yy === y),
      annee: y,
    }));
    // Les années d'avant la première facture ou dépense ne s'affichent pas :
    // une ligne « 2024 » vide s'affichait sans aucun document.
    const avecPieces = (y) =>
      pieces.some((f) => dans(f.date, (yy) => yy === y)) ||
      depenses.some((f) => (Number(f.montant) || 0) !== 0 && dans(f.date, (yy) => yy === y));
    while (lignes.length > 1 && !avecPieces(lignes[0].annee)) lignes.shift();
  }
  if (!lignes) {
    return null;
  }

  return {
    titre,
    lignes,
    totalCa: lignes.reduce((s, l) => s + l.ca, 0),
    totalTtc: lignes.reduce((s, l) => s + l.ttc, 0),
    totalBenefice: lignes.reduce((s, l) => s + l.benefice, 0),
  };
}
